import React, { useState, useEffect } from 'react';
import { Plus, MapPin, Building } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';
import BackButton from '../../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Warehouses = () => {
  const { toast } = useToast();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: ''
  });
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/inventory/warehouses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch warehouses');
      const data = await response.json();
      setWarehouses(data);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load warehouses',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.name || !formData.location) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive'
        });
        return;
      }

      const response = await fetch(`${API_URL}/api/inventory/warehouses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to create warehouse');

      toast({
        title: 'Success',
        description: 'Warehouse created successfully'
      });

      setShowModal(false);
      setFormData({ name: '', location: '', description: '' });
      fetchWarehouses();
    } catch (error) {
      console.error('Error creating warehouse:', error);
      toast({
        title: 'Error',
        description: 'Failed to create warehouse',
        variant: 'destructive'
      });
    }
  };

  return (
    <div>
      <BackButton />

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42]">Warehouses</h2>
            <p className="text-sm text-[#6C757D]">Manage storage locations</p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Warehouse
          </Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading warehouses...</p>
          </CardContent>
        </Card>
      ) : warehouses.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-[#6C757D] mb-4">No warehouses found</p>
              <Button onClick={() => setShowModal(true)} className="bg-[#00B4D8]">
                Create Your First Warehouse
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {warehouses.map((warehouse) => (
            <Card key={warehouse.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-3">
                  <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
                    <Building className="h-6 w-6 text-[#00B4D8]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#2B2D42] mb-1">
                      {warehouse.name}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-[#6C757D] mb-2">
                      <MapPin className="h-4 w-4" />
                      <span>{warehouse.location}</span>
                    </div>
                    {warehouse.description && (
                      <p className="text-sm text-[#6C757D] mt-2">{warehouse.description}</p>
                    )}
                    {warehouse.is_virtual && (
                      <Badge className="mt-2 bg-purple-100 text-purple-800">Dropshipping</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-[#2B2D42] mb-6">Create Warehouse</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Dubai Main Warehouse"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Dubai, UAE"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Optional description..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <Button onClick={() => setShowModal(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleCreate} className="bg-[#00B4D8]">
                  Create
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Warehouses;